import {
  ClientReadable,
  ClientsListData,
  clientsListInfiniteOptions,
  CurrenciesListListData,
  currenciesListListInfiniteOptions,
  CustomFieldRetrieveReadable,
  Dropdown,
  LeadsListListData,
  leadsListListInfiniteOptions,
  OpportunitiesStatusesListListData,
  opportunitiesStatusesListListInfiniteOptions,
  opportunitiesStatusesListOptions,
  PaginatedClientListReadable,
  PaginatedDropdownList,
  PaginatedReturnProductListReadable,
  PaginatedReturnUserListReadable,
  ProductsListData,
  productsListInfiniteOptions,
  ReturnOpportunityReadable,
  ReturnProductReadable,
  ReturnUserReadable,
  UsersListData,
  usersListInfiniteOptions
} from '@crm/api'
import { CustomField, InfiniteSelect } from '@crm/components'

import { listTransformResults, userTransformResults } from '@crm/utils'

import { Text } from '@shared/components'

import { transformOptions } from '@shared/utils'
import { DEFAULT_DATE_FORMAT } from '@shared/utils'
import { useQuery } from '@tanstack/react-query'
import {
  Col,
  DatePicker,
  Divider,
  Form,
  FormInstance,
  InputNumber,
  Row
} from 'antd'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  opportunityData?: ReturnOpportunityReadable
  form: FormInstance
  hiddenLeadSelect?: boolean
  leadId?: string | number
  clientId?: string | number
  filteredCustomFields?: CustomFieldRetrieveReadable[]
  checkboxCustomFields?: CustomFieldRetrieveReadable[]
}

export const OpportunityForm = ({
  opportunityData,
  form,

  leadId,
  clientId,
  filteredCustomFields,
  checkboxCustomFields
}: Props) => {
  const { t } = useTranslation()

  const pipelineValue = Form.useWatch('pipeline', form)

  const { data: statusesData } = useQuery({
    ...opportunitiesStatusesListOptions({
      query: {
        pipeline: pipelineValue ? Number(pipelineValue) : undefined,
        per_page: 1
      }
    })
  })

  useEffect(() => {
    if (!opportunityData && statusesData?.results?.[0]) {
      form.setFieldValue('status', statusesData.results[0].id)
    }
  }, [statusesData, opportunityData, form])

  return (
    <Row gutter={[12, 0]} align="bottom">
      {!clientId && (
        <Col span={12}>
          <Form.Item name="lead" label={t('opportunities.contact')}>
            <InfiniteSelect<LeadsListListData, PaginatedDropdownList, Dropdown>
              className="select-min-width"
              popupMatchSelectWidth
              placeholder={t('opportunities.contact')}
              getQueryFunction={leadsListListInfiniteOptions}
              disabled={!!leadId}
              baseOptions={{
                query: {
                  preference: opportunityData?.lead?.id
                    ? [opportunityData?.lead?.id]
                    : leadId
                    ? [leadId]
                    : undefined
                }
              }}
            />
          </Form.Item>
        </Col>
      )}

      {!!clientId && (
        <Col span={12}>
          <Form.Item name="client" label={t('lead.client')}>
            <InfiniteSelect<
              ClientsListData,
              PaginatedClientListReadable,
              ClientReadable
            >
              className="select-min-width"
              popupMatchSelectWidth
              placeholder={t('lead.client')}
              getQueryFunction={clientsListInfiniteOptions}
              disabled={!!clientId}
              getOptions={({ options }) =>
                transformOptions({ options, label: 'name', value: 'id' })
              }
              baseOptions={{
                query: {
                  preference: opportunityData?.client?.id
                    ? [opportunityData?.client?.id]
                    : clientId
                    ? [clientId]
                    : undefined
                }
              }}
            />
          </Form.Item>
        </Col>
      )}

      <Col span={12}>
        <Form.Item name="product" label={t('task.product')}>
          <InfiniteSelect<
            ProductsListData,
            PaginatedReturnProductListReadable,
            ReturnProductReadable
          >
            popupMatchSelectWidth
            placeholder={t('main.select')}
            getQueryFunction={productsListInfiniteOptions}
            getOptions={({ options }) =>
              transformOptions({ options, label: 'name', value: 'id' })
            }
          />
        </Form.Item>
      </Col>

      <Divider />

      <Col span={8}>
        <Form.Item name="value" label={t('opportunities.value')}>
          <InputNumber min={0} placeholder={t('opportunities.value')} />
        </Form.Item>
      </Col>

      <Col span={4}>
        <Form.Item name="currency" label={t('contracts.currency')}>
          <InfiniteSelect<
            CurrenciesListListData,
            PaginatedDropdownList,
            Dropdown
          >
            popupMatchSelectWidth
            placeholder={t('main.select')}
            getQueryFunction={currenciesListListInfiniteOptions}
            getOptions={({ options }) =>
              options.map(({ key, label }) => ({ label, value: key }))
            }
          />
        </Form.Item>
      </Col>

      <Col span={12}>
        <Form.Item name="close_date" label={t('task.close_date')}>
          <DatePicker
            format={DEFAULT_DATE_FORMAT}
            placeholder={t('task.close_date')}
            style={{ width: '100%' }}
          />
        </Form.Item>
      </Col>

      <Col span={12}>
        <Form.Item dependencies={['pipeline']} noStyle>
          {() => {
            const pipelineId = form.getFieldValue('pipeline')

            return (
              <Form.Item name="status" label={t('task.status')}>
                <InfiniteSelect<
                  OpportunitiesStatusesListListData,
                  PaginatedDropdownList,
                  Dropdown
                >
                  className="select-min-width"
                  popupMatchSelectWidth
                  placeholder={t('contacts.status')}
                  getOptions={({ options }) => listTransformResults(options)}
                  allowClear={false}
                  getQueryFunction={
                    opportunitiesStatusesListListInfiniteOptions
                  }
                  baseOptions={{
                    query: {
                      preference: opportunityData?.status?.pipeline?.id
                        ? [opportunityData?.status?.pipeline?.id]
                        : undefined,
                      pipeline: pipelineId ? Number(pipelineId) : undefined
                    }
                  }}
                />
              </Form.Item>
            )
          }}
        </Form.Item>
      </Col>

      <Col span={12}>
        <Form.Item
          name="owner"
          label={t('task.owner')}
          rules={[{ required: true }]}
        >
          <InfiniteSelect<
            UsersListData,
            PaginatedReturnUserListReadable,
            ReturnUserReadable
          >
            popupMatchSelectWidth
            placeholder={t('main.select')}
            getQueryFunction={usersListInfiniteOptions}
            getOptions={({ options }) => userTransformResults(options)}
          />
        </Form.Item>
      </Col>

      {((filteredCustomFields?.length ?? 0) > 0 ||
        (checkboxCustomFields?.length ?? 0) > 0) && (
        <Col span={24} className="mb-10">
          <Text size={16} weight={500}>
            {t('nomenclature.custom_fields')}
          </Text>
        </Col>
      )}

      {filteredCustomFields?.map((item) => (
        <Col span={12} key={item.id}>
          <CustomField {...item} />
        </Col>
      ))}

      {checkboxCustomFields?.map((item) => (
        <Col span={24} key={item.id}>
          <CustomField {...item} />
        </Col>
      ))}
    </Row>
  )
}
